// ============================================================
// useKeyboardShortcuts - Global keyboard shortcut handler
// ============================================================
// Extracted from DeathWatchSimulator.jsx. Handles:
//   - Space/Enter: attack
//   - t: reset target, r: reload, n: new tab, d: duplicate tab
//   - f: toggle free attack, c: clear narrative
//   - Tab/Shift+Tab: cycle tabs
//   - F1: toggle hotkeys, Escape: close modals
//   - 1-9: select weapon slot
//   - Ctrl+Z: undo
// ============================================================

import { useEffect } from 'react';
import { focusComposer } from '../utils/composerFocus';

const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

const VOX_KEYS = new Set(['`', '/']);
const hasModifier = (e) => e.ctrlKey || e.altKey || e.metaKey;

/** THE VOX (v8.363.4): ` puts the caret in the composer, / opens a command. */
const isVoxKey = (e) => VOX_KEYS.has(e.key) && !hasModifier(e);

/** A key that must not also scroll the page or move focus: prevent its default, then act. */
const claimed = (act) => (p, e) => {
  e.preventDefault();
  act(p, e);
};

/**
 * ESCAPE DISMISSES THE PROMPT IN FRONT OF YOU, NOT THE TAB YOU ARE ON.
 *
 * It used to clear `activeTab`'s defence, which was the same thing only
 * because a prompt could not appear for anybody else. Now it can, so it
 * clears the FOCUSED reaction - and clearing is a shared act: the document
 * is deleted for everyone and the attack is lost.
 *
 * Hence the guard. Escape reaches the shared document only when this
 * client has some claim on it (`escapeCanClear` - my actor, or GM), which
 * is exactly the reach the old `activeTab` version had by accident. For
 * everybody else Escape is inert on a reaction they are merely being
 * shown; they can still answer it, which is the safe direction, or leave
 * it to whoever it belongs to.
 */
function escape(p, e) {
  p.setShowHotkeys(false);
  p.setEditingSlot(null);
  p.setTabCtx(null);
  p.setSlotClipboard(null);
  // ESCAPE CLOSES THE THING IN FRONT, AND STOPS (2026-10-15). With one of the window's own overlays up (the hotkeys,
  // the slot editor, the tab menu: closed above) or pressed inside a dialog that closes on its own keydown
  // (AddConditionModal), the attack and the reaction stay. It used to close the overlay AND lose the attack.
  if (p.overlayOpen || e.target.closest?.('[role="dialog"]')) return;
  if (p.sessionId && p.clearPendingDefence) {
    if (p.escapeCanClear && p.focusedReactionId) p.clearPendingDefence(p.focusedReactionId);
  } else {
    p.setPendingAttack(null);
  }
}

/** The single-key bindings: a lookup, not a ladder. Each takes the hook's params and the event. */
const KEY_BINDINGS = new Map([
  [' ', claimed((p) => p.handleAttack())],
  ['Enter', claimed((p) => p.handleAttack())],
  ['t', (p) => p.doResetTarget()],
  ['r', (p) => p.handleReload()],
  ['n', (p) => p.handleNewTab()],
  ['d', (p) => p.handleDuplicateTab(p.activeTab, false)],
  ['f', (p) => p.toggleActionDrum()],
  ['e', (p) => p.endTurn?.()],
  ['c', (p) => p.doClearNarrative()],
  ['Tab', claimed((p, e) => p.cycleTab(e.shiftKey ? -1 : 1))],
  ['F1', claimed((p) => p.setShowHotkeys((h) => !h))],
  ['Escape', escape],
]);

/**
 * The Nth weapon on the rack, for 1-9. No filtering: a rack has no empty
 * plates, so its positions ARE the hotbar numbering (v8.189.0).
 */
function selectNth(p, e) {
  const nth = parseInt(e.key) - 1;
  if (p.currentSlots[nth]) p.setSelectedSlot(nth);
}

const isSlotKey = (e) => e.key >= '1' && e.key <= '9' && !e.ctrlKey;

// Each stage returns true when it took the key, which ends the search.

function voxStage(e) {
  if (!isVoxKey(e)) return false;
  if (focusComposer(e.key === '/' ? '/' : '')) e.preventDefault();
  return true;
}

function undoStage(e, p) {
  if (!e.ctrlKey || e.key !== 'z') return false;
  e.preventDefault();
  p.doUndo();
  return true;
}

/** An open dialog swallows every key but Escape, which closes it. */
function dialogStage(e, p) {
  if (!p.dialog) return false;
  if (e.key === 'Escape') p.handleDialogClose(null);
  return true;
}

function bindingStage(e, p) {
  const binding = KEY_BINDINGS.get(e.key) ?? (isSlotKey(e) ? selectNth : null);
  if (binding) binding(p, e);
  return true;
}

const STAGES = [voxStage, undoStage, dialogStage, bindingStage];

/**
 * An Escape something in front of the window already claimed (2026-10-15: useEscapeKey, ShellColumns). Run on here, it
 * also cancelled the attack and cleared the focused reaction for everyone, so closing a menu lost an attack.
 */
const escapeTaken = (e) => e.key === 'Escape' && e.defaultPrevented;

/** What a keydown does: a field keeps its keys; otherwise the first stage to take it. */
function onKey(e, p) {
  if (FIELD_TAGS.has(e.target.tagName) || escapeTaken(e)) return;
  STAGES.some((stage) => stage(e, p));
}

/**
 * @param {Object} params
 * @param {Function}    params.cycleTab
 * @param {Function}    [params.endTurn]  End the current turn (v8.320.0). Absent while the tracker is off.
 * @param {Function}    params.handleAttack
 * @param {Function}    params.doResetTarget
 * @param {Function}    params.handleReload
 * @param {Function}    params.handleNewTab
 * @param {Function}    params.handleDuplicateTab
 * @param {Function}    params.toggleActionDrum
 * @param {Function}    params.doClearNarrative
 * @param {Function}    params.setShowHotkeys
 * @param {Function}    params.setEditingSlot
 * @param {Function}    params.setTabCtx
 * @param {boolean}     [params.overlayOpen]  The hotkeys, the slot editor or the tab menu is up: Escape closes it only
 * @param {Function}    params.setPendingAttack
 * @param {Function}    params.setSlotClipboard
 * @param {Function}    params.setSelectedSlot
 * @param {Function}    params.doUndo
 * @param {string}      params.activeTab
 * @param {string|null} params.focusedReactionId  The reaction Escape would dismiss
 * @param {boolean}     params.escapeCanClear     May this client dismiss it for everyone?
 * @param {Array}       params.currentSlots
 * @param {Object|null} params.dialog
 * @param {Function}    params.handleDialogClose
 * @param {Function}    [params.clearPendingDefence]
 * @param {string|null} params.sessionId
 */
export default function useKeyboardShortcuts(params) {
  // No dependency list, deliberately: the listener is re-bound every
  // render, so it always sees the current params.
  useEffect(() => {
    const handler = (e) => onKey(e, params);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });
}
